/**
 * Iter 41.38 — Lightweight Commercial Billing tab (per-client).
 *
 * Embeds into AdminClientProfile.jsx without modifying any other tab.
 * Visible to hm_admin (full write) + hm_advisor (read-only).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Wallet, FileDown, Plus, Pencil, Trash2, DollarSign, Repeat, Sparkles,
  AlertTriangle, CheckCircle2, Loader2, ReceiptText, Banknote,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { getUiLocale, bizDate } from '@/lib/dates';
import { suiteLabel, CRM_SUITE_OPTIONS } from '@/lib/crmSuites';
import LocalizedDateInput from '@/components/shared/LocalizedDateInput';

// Suites billable through standard subscriptions. Global Ethics is
// intentionally excluded: it is a separate governed service arrangement.
const BILLING_SUITES = CRM_SUITE_OPTIONS.filter((c) => c !== 'global_ethics');
const CURRENCIES = ['USD', 'EUR', 'PAB', 'MXN', 'COP', 'CRC', 'ARS', 'BRL', 'CLP', 'PEN'];
const SUBSCRIPTION_TYPES = ['monthly', 'annual', 'one_time_project'];
const STATUS_TONE = {
  active:        'bg-success/10 text-success border-success/30',
  expiring_soon: 'bg-warning/10 text-warning border-warning/30',
  expired:       'bg-destructive/10 text-destructive border-destructive/30',
  suspended:     'bg-muted text-muted-foreground border-border',
};

const fmtMoney = (n, currency) => {
  if (n == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: currency || 'USD',
      maximumFractionDigits: 2, minimumFractionDigits: 0,
    }).format(Number(n));
  } catch {
    return `${currency || ''} ${Number(n).toLocaleString(getUiLocale())}`;
  }
};

const bulkEffectiveMonthly = (f) => (f.subscription_type === 'annual'
  ? Math.round((Number(f.annual_value || 0) / 12) * 100) / 100
  : Number(f.monthly_value || 0));
const bulkEffectiveAnnual = (f) => (f.subscription_type === 'monthly'
  ? Number(f.monthly_value || 0) * 12
  : Number(f.annual_value || 0));
const computeBulkRenewal = (f) => {
  const d = new Date(`${f.start_date || new Date().toISOString().slice(0, 10)}T00:00:00`);
  if (f.subscription_type === 'monthly') d.setDate(d.getDate() + 30);
  else if (f.subscription_type === 'annual') d.setDate(d.getDate() + 365);
  return d.toISOString().slice(0, 10);
};

export default function BillingTab({ companyId, companySuiteCodes = [] }) {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const isAdmin = user?.role === 'hm_admin';

  const [profile, setProfile] = useState(null);
  const [subs, setSubs]       = useState([]);
  const [stmt, setStmt]       = useState({ entries: [], current_balance: 0, outstanding_balance: 0 });
  const [pays, setPays]       = useState([]);
  const [upsell, setUpsell]   = useState(null);
  const [loading, setLoading] = useState(true);
  // dialogs
  const [editSub, setEditSub] = useState(null);     // subscription being edited (or {} for new)
  const [bulkOpen, setBulkOpen]   = useState(false);
  const [bulkSuites, setBulkSuites] = useState([]);
  const [bulkForm, setBulkForm]   = useState({ subscription_type: 'monthly', monthly_value: 0, annual_value: 0, currency: 'USD', start_date: new Date().toISOString().slice(0,10) });
  const [payOpen, setPayOpen]     = useState(false);
  const [payForm, setPayForm]     = useState({ payment_date: new Date().toISOString().slice(0,10), amount: 0, currency: 'USD', payment_method: 'bank_transfer', reference: '', notes: '' });
  const [adjOpen, setAdjOpen]     = useState(false);
  const [adjForm, setAdjForm]     = useState({ date: new Date().toISOString().slice(0,10), description: '', charge: 0, credit: 0, currency: 'USD' });
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ billing_currency: 'USD', billing_status: 'active', client_since: '' });

  const reload = async () => {
    setLoading(true);
    try {
      const [p, s, st, py, up] = await Promise.all([
        adminApi.getBillingProfile(companyId),
        adminApi.listSubscriptions(companyId),
        adminApi.getStatement(companyId),
        adminApi.listPayments(companyId),
        adminApi.upsellOpportunities(companyId),
      ]);
      setProfile(p); setSubs(s); setStmt(st); setPays(py); setUpsell(up);
      setBulkForm((f) => ({ ...f, currency: p.billing_currency || 'USD' }));
      setPayForm((f) => ({ ...f, currency: p.billing_currency || 'USD' }));
      setAdjForm((f) => ({ ...f, currency: p.billing_currency || 'USD' }));
      setProfileForm({
        billing_currency: p.billing_currency || 'USD',
        billing_status:   p.billing_status   || 'active',
        client_since:     p.client_since ? String(p.client_since).slice(0,10) : '',
      });
    } catch (e) {
      toast.error(t('pages.billing.loadFailed', { defaultValue: 'Failed to load billing' }),
                  { description: formatApiError(e) });
    } finally { setLoading(false); }
  };
  useEffect(() => { if (companyId) reload(); /* eslint-disable-next-line */ }, [companyId]);

  const detectedMissing = useMemo(() => {
    if (!subs) return [];
    const billed = new Set(subs.filter((s) => s.status !== 'expired').map((s) => s.suite_code));
    return (companySuiteCodes || []).filter((c) => !billed.has(c));
  }, [companySuiteCodes, subs]);

  const downloadPdf = () => {
    const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
    api.get(adminApi.statementPdfUrl(companyId, lang), { responseType: 'blob' })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `account_statement_${companyId.slice(0,8)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(t('pages.billing.pdfFailed', { defaultValue: 'PDF failed' }),
                                { description: formatApiError(e) }));
  };

  if (loading || !profile) {
    return (
      <Card className="p-6 shadow-card" data-testid="billing-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> {t('common.loading')}
      </Card>
    );
  }

  const currency = profile.billing_currency || 'USD';

  return (
    <div className="space-y-6" data-testid="billing-tab">
      {/* Profile header */}
      <Card className="p-6 shadow-card" data-testid="billing-profile">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              {t('pages.billing.title', { defaultValue: 'Subscriptions & Billing' })}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('pages.billing.subtitle', {
                defaultValue: 'Commercial management — not an accounting or tax module.',
              })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => setProfileOpen(true)} data-testid="billing-edit-profile">
                <Pencil className="h-4 w-4 mr-1" /> {t('common.edit', { defaultValue: 'Edit' })}
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="billing-pdf">
                <FileDown className="h-4 w-4 mr-1" /> {t('pages.billing.pdf', { defaultValue: 'Account Statement PDF' })}
              </Button>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 text-sm">
          <Tile label={t('pages.billing.currency', { defaultValue: 'Billing currency' })}    value={currency} />
          <Tile label={t('pages.billing.billingStatus', { defaultValue: 'Billing status' })} value={t(`pages.billing.statusValue.${profile.billing_status || 'active'}`, { defaultValue: profile.billing_status })} />
          <Tile label={t('pages.billing.clientSince', { defaultValue: 'Client since' })}     value={profile.client_since ? bizDate(String(profile.client_since).slice(0, 10), i18n.language) : '—'} />
          <Tile label={t('pages.billing.nextRenewal', { defaultValue: 'Next renewal' })}     value={profile.next_renewal_date ? bizDate(profile.next_renewal_date, i18n.language) : '—'} />
          {profile.current_balance < 0 ? (
            <Tile label={t('pages.billing.creditBalance', { defaultValue: 'Credit balance' })} value={fmtMoney(Math.abs(profile.current_balance), currency)} tone="success" />
          ) : (
            <Tile label={t('pages.billing.currentBalance', { defaultValue: 'Current balance' })} value={fmtMoney(profile.current_balance, currency)} />
          )}
          <Tile label={t('pages.billing.outstanding', { defaultValue: 'Outstanding balance' })} value={fmtMoney(profile.outstanding_balance, currency)} tone={profile.outstanding_balance > 0 ? 'warning' : 'muted'} />
          <Tile label={t('pages.billing.lastPayment', { defaultValue: 'Last payment' })}     value={profile.last_payment_date ? bizDate(profile.last_payment_date, i18n.language) : '—'} />
          <Tile label={t('pages.billing.advisor', { defaultValue: 'Advisor' })}              value={profile.assigned_advisor_name || '—'} />
        </div>
      </Card>

      {/* Detected missing subscriptions — visible to admin (actionable) and advisor (read-only) */}
      {detectedMissing.length > 0 && (
        <Card className="p-4 shadow-card border-warning/40 bg-warning/5" data-testid="billing-detected">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">
                  {t('pages.billing.detected.title', { defaultValue: 'Detected suites without billing' })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {detectedMissing.map((c) => suiteLabel(c)).join(' · ')}
                </p>
              </div>
            </div>
            {isAdmin && (
              <Button onClick={() => { setBulkSuites(detectedMissing); setBulkOpen(true); }} size="sm" data-testid="billing-bulk-add">
                <Plus className="h-4 w-4 mr-1" />
                {t('pages.billing.detected.bulkAdd', { defaultValue: 'Bulk-add subscriptions' })}
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Subscriptions */}
      <Card className="p-6 shadow-card" data-testid="billing-subscriptions">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Repeat className="h-4 w-4 text-primary" />
            {t('pages.billing.activeSuites', { defaultValue: 'Billed Subscriptions' })}
          </h3>
          {isAdmin && (
            <Button size="sm" onClick={() => setEditSub({})} data-testid="billing-add-subscription">
              <Plus className="h-4 w-4 mr-1" /> {t('pages.billing.addSub', { defaultValue: 'New subscription' })}
            </Button>
          )}
        </div>
        {subs.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            {t('pages.billing.noSubs', { defaultValue: "No billing subscriptions configured yet. Billing records are managed manually by Corporate Talent Hub and are independent of the client's assigned suites." })}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="py-2">{t('pages.billing.suite', { defaultValue: 'Suite' })}</th>
                  <th className="py-2">{t('pages.billing.startDate', { defaultValue: 'Start' })}</th>
                  <th className="py-2">{t('pages.billing.renewalDate', { defaultValue: 'Renewal' })}</th>
                  <th className="py-2">{t('pages.billing.subscriptionType', { defaultValue: 'Type' })}</th>
                  <th className="py-2 text-right">{t('pages.billing.monthlyValue', { defaultValue: 'Monthly' })}</th>
                  <th className="py-2 text-right">{t('pages.billing.annualValue', { defaultValue: 'Annual' })}</th>
                  <th className="py-2">{t('pages.billing.status', { defaultValue: 'Status' })}</th>
                  {isAdmin && <th />}
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t" data-testid={`billing-sub-row-${s.id}`}>
                    <td className="py-2 font-medium">{suiteLabel(s.suite_code)}</td>
                    <td className="py-2">{s.start_date ? bizDate(s.start_date, i18n.language) : '—'}</td>
                    <td className="py-2">{s.renewal_date ? bizDate(s.renewal_date, i18n.language) : '—'}</td>
                    <td className="py-2">{t(`pages.billing.subType.${s.subscription_type}`, { defaultValue: s.subscription_type })}</td>
                    <td className="py-2 text-right">{fmtMoney(s.monthly_value, s.currency)}</td>
                    <td className="py-2 text-right">{fmtMoney(s.annual_value, s.currency)}</td>
                    <td className="py-2">
                      <Badge variant="outline" className={STATUS_TONE[s.status]}>
                        {t(`pages.billing.statusValue.${s.status}`, { defaultValue: s.status })}
                      </Badge>
                    </td>
                    {isAdmin && (
                      <td className="py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => setEditSub(s)} data-testid={`billing-sub-edit-${s.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={async () => {
                          if (!window.confirm(t('common.confirmDelete', { defaultValue: 'Delete this row?' }))) return;
                          try {
                            await adminApi.deleteSubscription(s.id);
                            toast.success(t('common.deleted', { defaultValue: 'Deleted' }));
                            reload();
                          } catch (e) {
                            toast.error(formatApiError(e));
                          }
                        }} data-testid={`billing-sub-delete-${s.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3" data-testid="billing-global-ethics-note">
          {t('pages.billing.globalEthicsNote', {
            defaultValue: 'Global Ethics is managed through a separate governed service arrangement and is not billed as a suite subscription.',
          })}
        </p>
      </Card>

      {/* Account statement */}
      <Card className="p-6 shadow-card" data-testid="billing-statement">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <ReceiptText className="h-4 w-4 text-primary" />
            {t('pages.billing.statement', { defaultValue: 'Account Statement' })}
          </h3>
          {isAdmin && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setAdjOpen(true)} data-testid="billing-add-adjustment">
                <Plus className="h-4 w-4 mr-1" /> {t('pages.billing.addAdjustment', { defaultValue: 'Register movement' })}
              </Button>
              <Button size="sm" onClick={() => setPayOpen(true)} className="bg-success hover:bg-success/90 text-white" data-testid="billing-register-payment">
                <Banknote className="h-4 w-4 mr-1" /> {t('pages.billing.registerPayment', { defaultValue: 'Register payment' })}
              </Button>
            </div>
          )}
        </div>
        {stmt.entries.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">{t('pages.billing.noEntries', { defaultValue: 'No entries.' })}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="py-2">{t('pages.billing.date', { defaultValue: 'Date' })}</th>
                  <th className="py-2">{t('pages.billing.description', { defaultValue: 'Description' })}</th>
                  <th className="py-2 text-right">{t('pages.billing.charge', { defaultValue: 'Charge' })}</th>
                  <th className="py-2 text-right">{t('pages.billing.credit', { defaultValue: 'Credit' })}</th>
                  <th className="py-2 text-right">{t('pages.billing.balance', { defaultValue: 'Balance' })}</th>
                </tr>
              </thead>
              <tbody>
                {stmt.entries.map((e) => {
                  let desc = e.description || '';
                  if (e.entry_type === 'payment_received') {
                    if (desc.startsWith('Payment received')) desc = desc.replace(/^Payment received\s*[—-]?\s*/, '');
                    desc = `${t('pages.billing.entryType.payment_received', { defaultValue: 'Payment received' })}${desc ? ` — ${desc}` : ''}`;
                  }
                  return (
                  <tr key={e.id} className="border-t" data-testid={`billing-entry-row-${e.id}`}>
                    <td className="py-2">{e.date ? bizDate(e.date, i18n.language) : '—'}</td>
                    <td className="py-2">{desc}</td>
                    <td className="py-2 text-right">{e.charge ? fmtMoney(e.charge, e.currency) : '—'}</td>
                    <td className="py-2 text-right text-success">{e.credit ? fmtMoney(e.credit, e.currency) : '—'}</td>
                    <td className="py-2 text-right font-medium">{fmtMoney(e.balance, e.currency)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 flex justify-end gap-6 text-sm" data-testid="billing-statement-totals">
          <span className="text-muted-foreground">
            {stmt.current_balance < 0
              ? <>{t('pages.billing.creditBalance', { defaultValue: 'Credit balance' })}:{' '}
                  <strong className="text-success">{fmtMoney(Math.abs(stmt.current_balance), currency)}</strong></>
              : <>{t('pages.billing.currentBalance', { defaultValue: 'Current Balance' })}:{' '}
                  <strong className="text-foreground">{fmtMoney(stmt.current_balance, currency)}</strong></>}
          </span>
          <span className="text-muted-foreground">
            {t('pages.billing.outstanding', { defaultValue: 'Outstanding' })}:{' '}
            <strong className={stmt.outstanding_balance > 0 ? 'text-warning' : 'text-foreground'}>
              {fmtMoney(stmt.outstanding_balance, currency)}
            </strong>
          </span>
        </div>
      </Card>

      {/* Payments */}
      <Card className="p-6 shadow-card" data-testid="billing-payments">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Banknote className="h-4 w-4 text-success" />
          {t('pages.billing.payments', { defaultValue: 'Payment history' })}
        </h3>
        {pays.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            {t('pages.billing.noPayments', { defaultValue: 'No payments registered yet.' })}
          </p>
        ) : (
          <ul className="space-y-2">
            {pays.map((p) => (
              <li key={p.id} className="flex items-center justify-between border-t pt-2 text-sm gap-3 flex-wrap" data-testid={`billing-payment-row-${p.id}`}>
                <span>
                  {bizDate(p.payment_date, i18n.language)}
                  {p.payment_method && (
                    <Badge variant="outline" className="ml-2 text-xs">{t(`pages.billing.pay.methodValue.${p.payment_method}`, { defaultValue: p.payment_method })}</Badge>
                  )}
                  {p.reference && <span className="text-muted-foreground ml-2 text-xs">Ref: {p.reference}</span>}
                  {p.notes && <span className="text-muted-foreground ml-2">· {p.notes}</span>}
                </span>
                <span className="font-medium text-success">{fmtMoney(p.amount, p.currency)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Upsell */}
      {upsell && upsell.available?.length > 0 && (
        <Card className="p-6 shadow-card bg-primary/5 border-primary/30" data-testid="billing-upsell">
          <h3 className="font-semibold flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {t('pages.billing.upsell.title', { defaultValue: 'Expansion opportunities' })}
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            {t('pages.billing.upsell.subtitle', {
              defaultValue: 'Suites not yet contracted by this client.',
            })}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {upsell.available.map((c) => (
              <Badge key={c} variant="outline" className="bg-card">{suiteLabel(c)}</Badge>
            ))}
          </div>
          <p className="text-sm mt-3" data-testid="billing-upsell-estimate">
            {t('pages.billing.upsell.estimate', {
              defaultValue: 'Estimated additional monthly revenue',
            })}:{' '}
            <strong className="text-primary">
              {fmtMoney(upsell.estimated_total_monthly, upsell.currency)}
            </strong>
            <span className="text-xs text-muted-foreground ml-2">
              ({fmtMoney(upsell.estimated_monthly_per_suite, upsell.currency)} ×{' '}
              {upsell.available.length}{' '}
              {t('pages.billing.upsell.suites', { defaultValue: 'suites' })})
            </span>
          </p>
        </Card>
      )}

      {/* === Dialogs === */}

      {/* Billing profile edit */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent data-testid="billing-profile-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.billing.editProfile.title', { defaultValue: 'Edit billing profile' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>{t('pages.billing.currency', { defaultValue: 'Billing currency' })}</Label>
              <Select value={profileForm.billing_currency} onValueChange={(v) => setProfileForm((f) => ({ ...f, billing_currency: v }))}>
                <SelectTrigger data-testid="bp-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('pages.billing.billingStatus', { defaultValue: 'Billing status' })}</Label>
              <Select value={profileForm.billing_status} onValueChange={(v) => setProfileForm((f) => ({ ...f, billing_status: v }))}>
                <SelectTrigger data-testid="bp-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['active', 'suspended', 'inactive'].map((s) => (
                    <SelectItem key={s} value={s}>{t(`pages.billing.statusValue.${s}`, { defaultValue: s })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('pages.billing.clientSince', { defaultValue: 'Client since' })}</Label>
              <LocalizedDateInput value={profileForm.client_since}
                     onChange={(v) => setProfileForm((f) => ({ ...f, client_since: v }))}
                     data-testid="bp-clientsince" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={async () => {
              try {
                await adminApi.updateBillingProfile(companyId, profileForm);
                toast.success(t('common.saved', { defaultValue: 'Saved' }));
                setProfileOpen(false);
                reload();
              } catch (e) {
                toast.error(formatApiError(e));
              }
            }} data-testid="bp-save">{t('common.save', { defaultValue: 'Save' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit subscription */}
      <SubscriptionDialog
        open={!!editSub}
        onOpenChange={(v) => !v && setEditSub(null)}
        existing={editSub}
        companyId={companyId}
        defaultCurrency={currency}
        companySuiteCodes={companySuiteCodes}
        onSaved={() => { setEditSub(null); reload(); }}
      />

      {/* Bulk-add subscriptions */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="billing-bulk-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.billing.bulk.title', { defaultValue: 'Bulk-add subscriptions' })}</DialogTitle>
            <DialogDescription>
              {t('pages.billing.bulk.desc', {
                defaultValue: 'Apply the same values to every detected suite without billing.',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p>{t('pages.billing.bulk.selectSuites', { defaultValue: 'Select the suites to bill:' })}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setBulkSuites(detectedMissing)} data-testid="bulk-select-all">
                  {t('pages.billing.bulk.selectAll', { defaultValue: 'Select all' })}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setBulkSuites([])} data-testid="bulk-clear-all">
                  {t('pages.billing.bulk.clearAll', { defaultValue: 'Clear all' })}
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="bulk-suites">
              {detectedMissing.map((c) => {
                const sel = bulkSuites.includes(c);
                return (
                  <button key={c} type="button"
                          onClick={() => setBulkSuites((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]))}
                          className={`px-2.5 py-1 rounded-full text-xs border transition ${sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-foreground border-border hover:bg-muted'}`}
                          data-testid={`bulk-suite-${c}`}>
                    {suiteLabel(c)}
                  </button>
                );
              })}
            </div>
            <p className="text-xs font-medium text-warning" data-testid="bulk-per-suite-note">
              {t('pages.billing.bulk.perSuite', { defaultValue: 'These values will be applied to each suite individually.' })}
            </p>
            <div>
              <Label>{t('pages.billing.subscriptionType', { defaultValue: 'Type' })}</Label>
              <Select value={bulkForm.subscription_type} onValueChange={(v) => setBulkForm({ ...bulkForm, subscription_type: v })}>
                <SelectTrigger data-testid="bulk-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUBSCRIPTION_TYPES.map((st) => (
                    <SelectItem key={st} value={st}>{t(`pages.billing.subType.${st}`, { defaultValue: st })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('pages.billing.monthlyValue', { defaultValue: 'Monthly value' })}{bulkForm.subscription_type === 'annual' && ` ${t('pages.billing.calculated', { defaultValue: '(calculated)' })}`}</Label>
                <Input type="number" min="0"
                       value={bulkForm.subscription_type === 'annual' ? Math.round((bulkForm.annual_value / 12) * 100) / 100 : bulkForm.monthly_value}
                       onChange={(e) => setBulkForm({ ...bulkForm, monthly_value: Number(e.target.value) })}
                       readOnly={bulkForm.subscription_type === 'annual'}
                       className={bulkForm.subscription_type === 'annual' ? 'bg-muted' : ''}
                       data-testid="bulk-monthly" />
              </div>
              <div>
                <Label>{t('pages.billing.annualValue', { defaultValue: 'Annual value' })}{bulkForm.subscription_type === 'monthly' && ` ${t('pages.billing.calculated', { defaultValue: '(calculated)' })}`}</Label>
                <Input type="number" min="0"
                       value={bulkForm.subscription_type === 'monthly' ? bulkForm.monthly_value * 12 : bulkForm.annual_value}
                       onChange={(e) => setBulkForm({ ...bulkForm, annual_value: Number(e.target.value) })}
                       readOnly={bulkForm.subscription_type === 'monthly'}
                       className={bulkForm.subscription_type === 'monthly' ? 'bg-muted' : ''}
                       data-testid="bulk-annual" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('pages.billing.startDate', { defaultValue: 'Start date' })}</Label>
                <LocalizedDateInput value={bulkForm.start_date} onChange={(v) => setBulkForm({ ...bulkForm, start_date: v })} data-testid="bulk-start" />
              </div>
              <div>
                <Label>{t('pages.billing.currency', { defaultValue: 'Billing currency' })}</Label>
                <Select value={bulkForm.currency} onValueChange={(v) => setBulkForm({ ...bulkForm, currency: v })}>
                  <SelectTrigger data-testid="bulk-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="bulk-renewal-preview">
              {t('pages.billing.bulk.renewalPreview', { defaultValue: 'Renewal date (auto-calculated)' })}:{' '}
              <strong>{bizDate(computeBulkRenewal(bulkForm), i18n.language)}</strong>
            </p>
            {/* Financial impact summary */}
            <div className="rounded-md border bg-muted/40 p-3 space-y-1" data-testid="bulk-impact">
              <p className="font-medium">{t('pages.billing.bulk.impact', { defaultValue: 'Estimated financial impact' })}</p>
              <p className="text-xs text-muted-foreground">
                {bulkSuites.length} {t('pages.billing.upsell.suites', { defaultValue: 'suites' })} ×{' '}
                {fmtMoney(bulkEffectiveMonthly(bulkForm), bulkForm.currency)} /{' '}
                {t('pages.billing.bulk.perMonth', { defaultValue: 'month per suite' })}
              </p>
              <p data-testid="bulk-total-monthly">
                {t('pages.billing.bulk.totalMonthly', { defaultValue: 'Total monthly' })}:{' '}
                <strong>{fmtMoney(bulkEffectiveMonthly(bulkForm) * bulkSuites.length, bulkForm.currency)}</strong>
              </p>
              <p data-testid="bulk-total-annual">
                {t('pages.billing.bulk.totalAnnual', { defaultValue: 'Total annual equivalent' })}:{' '}
                <strong>{fmtMoney(bulkEffectiveAnnual(bulkForm) * bulkSuites.length, bulkForm.currency)}</strong>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>{t('common.cancel')}</Button>
            <Button disabled={bulkSuites.length === 0} onClick={async () => {
              try {
                const r = await adminApi.bulkAddSubscriptions(companyId, { ...bulkForm, only_codes: bulkSuites });
                toast.success(t('pages.billing.bulk.created', { n: r.created.length, defaultValue: '{{n}} subscriptions created' }));
                setBulkOpen(false);
                reload();
              } catch (e) {
                toast.error(formatApiError(e));
              }
            }} data-testid="bulk-submit">{t('pages.billing.bulk.confirm', { defaultValue: 'Create' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Register payment — readable typography at 100% zoom (Founder visual closeout) */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="billing-pay-dialog">
          <DialogHeader><DialogTitle>{t('pages.billing.pay.title', { defaultValue: 'Register payment' })}</DialogTitle></DialogHeader>
          <div className="space-y-4 text-base">
            <p className="text-sm leading-relaxed text-muted-foreground" data-testid="pay-applies-note">
              {t('pages.billing.pay.appliesNote', {
                defaultValue: "The payment is recorded as a credit against the client's general account balance; it is not applied to a specific subscription.",
              })}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('pages.billing.date', { defaultValue: 'Date' })}</Label>
                <LocalizedDateInput className="h-10 text-base" value={payForm.payment_date} onChange={(v) => setPayForm({ ...payForm, payment_date: v })} data-testid="pay-date" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('pages.billing.amount', { defaultValue: 'Amount' })}</Label>
                <Input type="number" min="0.01" step="0.01" className="h-10 md:text-base" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: Number(e.target.value) })} data-testid="pay-amount" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('pages.billing.pay.method', { defaultValue: 'Payment method' })}</Label>
              <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                <SelectTrigger className="h-10 text-base" data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['bank_transfer', 'check', 'card', 'cash', 'other'].map((m) => (
                    <SelectItem key={m} value={m} className="text-base">{t(`pages.billing.pay.methodValue.${m}`, { defaultValue: m })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('pages.billing.currency', { defaultValue: 'Billing currency' })}</Label>
                <Select value={payForm.currency} onValueChange={(v) => setPayForm({ ...payForm, currency: v })}>
                  <SelectTrigger className="h-10 text-base" data-testid="pay-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c} className="text-base">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('pages.billing.pay.reference', { defaultValue: 'Payment reference / transaction number' })}</Label>
                <Input className="h-10 md:text-base" value={payForm.reference} maxLength={120} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} data-testid="pay-reference" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('pages.billing.notes', { defaultValue: 'Notes' })}</Label>
              <Textarea rows={2} className="md:text-base" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} data-testid="pay-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={async () => {
              if (!payForm.amount || payForm.amount <= 0) {
                toast.error(t('pages.billing.pay.invalidAmount', { defaultValue: 'Enter a positive amount' }));
                return;
              }
              try {
                await adminApi.registerPayment(companyId, payForm);
                toast.success(t('pages.billing.pay.registered', { defaultValue: 'Payment registered' }));
                setPayOpen(false);
                reload();
              } catch (e) {
                toast.error(formatApiError(e));
              }
            }} className="bg-success hover:bg-success/90 text-white" data-testid="pay-submit">
              {t('pages.billing.pay.submit', { defaultValue: 'Register' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual adjustment */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent data-testid="billing-adj-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.billing.adj.title', { defaultValue: 'Register movement' })}</DialogTitle>
            <DialogDescription>
              {t('pages.billing.adj.desc', { defaultValue: 'Add a manual charge or credit to the account statement.' })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('pages.billing.date', { defaultValue: 'Date' })}</Label>
                <LocalizedDateInput value={adjForm.date} onChange={(v) => setAdjForm({ ...adjForm, date: v })} data-testid="adj-date" />
              </div>
              <div>
                <Label>{t('pages.billing.currency', { defaultValue: 'Billing currency' })}</Label>
                <Select value={adjForm.currency} onValueChange={(v) => setAdjForm({ ...adjForm, currency: v })}>
                  <SelectTrigger data-testid="adj-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('pages.billing.description', { defaultValue: 'Description' })}</Label>
              <Input value={adjForm.description} onChange={(e) => setAdjForm({ ...adjForm, description: e.target.value })} data-testid="adj-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('pages.billing.charge', { defaultValue: 'Charge' })}</Label>
                <Input type="number" min="0" value={adjForm.charge} onChange={(e) => setAdjForm({ ...adjForm, charge: Number(e.target.value) })} data-testid="adj-charge" />
              </div>
              <div>
                <Label>{t('pages.billing.credit', { defaultValue: 'Credit' })}</Label>
                <Input type="number" min="0" value={adjForm.credit} onChange={(e) => setAdjForm({ ...adjForm, credit: Number(e.target.value) })} data-testid="adj-credit" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('pages.billing.adj.exactlyOneHint', { defaultValue: 'Enter only one: a charge or a credit (greater than 0).' })}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={async () => {
              if (adjForm.charge < 0 || adjForm.credit < 0) {
                toast.error(t('pages.billing.adj.negative', { defaultValue: 'Values cannot be negative' }));
                return;
              }
              if (!adjForm.charge && !adjForm.credit) {
                toast.error(t('pages.billing.adj.amountMissing', { defaultValue: 'You must enter either a charge or a credit greater than 0.' }));
                return;
              }
              if (adjForm.charge > 0 && adjForm.credit > 0) {
                toast.error(t('pages.billing.adj.exactlyOne', { defaultValue: 'Enter only a charge OR a credit, not both' }));
                return;
              }
              if (!adjForm.description.trim()) {
                toast.error(t('pages.billing.adj.descMissing', { defaultValue: 'Description required' }));
                return;
              }
              try {
                await adminApi.createManualEntry(companyId, adjForm);
                toast.success(t('common.saved', { defaultValue: 'Saved' }));
                setAdjOpen(false);
                reload();
              } catch (e) {
                toast.error(formatApiError(e));
              }
            }} data-testid="adj-submit">{t('common.save', { defaultValue: 'Save' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ label, value, tone }) {
  const cls = tone === 'warning' ? 'text-warning' : tone === 'success' ? 'text-success' : tone === 'muted' ? 'text-muted-foreground' : 'text-foreground';
  return (
    <div>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">{label}</p>
      <p className={`text-sm font-medium ${cls}`}>{value}</p>
    </div>
  );
}

function SubscriptionDialog({ open, onOpenChange, existing, companyId, defaultCurrency, companySuiteCodes, onSaved }) {
  const { t } = useTranslation();
  const isNew = !existing?.id;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setForm({
      suite_code:        existing.suite_code || (companySuiteCodes?.[0] || 'talentmap'),
      subscription_type: existing.subscription_type || 'monthly',
      monthly_value:     existing.monthly_value ?? 0,
      annual_value:      existing.annual_value ?? 0,
      currency:          existing.currency || defaultCurrency || 'USD',
      start_date:        existing.start_date || new Date().toISOString().slice(0,10),
      renewal_date:      existing.renewal_date || '',
      status:            existing.status || 'active',
    });
  }, [existing, defaultCurrency, companySuiteCodes]);

  const submit = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await adminApi.createSubscription(companyId, form);
        toast.success(t('common.created', { defaultValue: 'Created' }));
      } else {
        await adminApi.patchSubscription(existing.id, form);
        toast.success(t('common.saved', { defaultValue: 'Saved' }));
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-sub-dialog">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? t('pages.billing.sub.titleCreate', { defaultValue: 'New subscription' })
              : t('pages.billing.sub.titleEdit', { defaultValue: 'Edit subscription' })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {isNew && (
            <div>
              <Label>{t('pages.billing.suite', { defaultValue: 'Suite' })}</Label>
              <Select value={form.suite_code} onValueChange={(v) => setForm({ ...form, suite_code: v })}>
                <SelectTrigger data-testid="sub-suite"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(companySuiteCodes?.length ? companySuiteCodes : BILLING_SUITES).map((c) => (
                    <SelectItem key={c} value={c}>{suiteLabel(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>{t('pages.billing.subscriptionType', { defaultValue: 'Subscription type' })}</Label>
            <Select value={form.subscription_type} onValueChange={(v) => setForm({ ...form, subscription_type: v })}>
              <SelectTrigger data-testid="sub-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUBSCRIPTION_TYPES.map((st) => (
                  <SelectItem key={st} value={st}>{t(`pages.billing.subType.${st}`, { defaultValue: st })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('pages.billing.monthlyValue', { defaultValue: 'Monthly value' })}{form.subscription_type === 'annual' && ` ${t('pages.billing.calculated', { defaultValue: '(calculated)' })}`}</Label>
              <Input type="number" min="0"
                     value={form.subscription_type === 'annual'
                       ? Math.round((Number(form.annual_value || 0) / 12) * 100) / 100
                       : (form.monthly_value ?? 0)}
                     onChange={(e) => setForm({ ...form, monthly_value: Number(e.target.value) })}
                     readOnly={form.subscription_type === 'annual'}
                     className={form.subscription_type === 'annual' ? 'bg-muted' : ''}
                     data-testid="sub-monthly" />
            </div>
            <div>
              <Label>{t('pages.billing.annualValue', { defaultValue: 'Annual value' })}{form.subscription_type === 'monthly' && ` ${t('pages.billing.calculated', { defaultValue: '(calculated)' })}`}</Label>
              <Input type="number" min="0"
                     value={form.subscription_type === 'monthly'
                       ? Number(form.monthly_value || 0) * 12
                       : (form.annual_value ?? 0)}
                     onChange={(e) => setForm({ ...form, annual_value: Number(e.target.value) })}
                     readOnly={form.subscription_type === 'monthly'}
                     className={form.subscription_type === 'monthly' ? 'bg-muted' : ''}
                     data-testid="sub-annual" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('pages.billing.startDate', { defaultValue: 'Start date' })}</Label>
              <LocalizedDateInput value={form.start_date || ''} onChange={(v) => setForm({ ...form, start_date: v })} data-testid="sub-start" />
            </div>
            <div>
              <Label>{t('pages.billing.renewalDate', { defaultValue: 'Renewal date' })}</Label>
              <LocalizedDateInput value={form.renewal_date || ''} onChange={(v) => setForm({ ...form, renewal_date: v })} data-testid="sub-renewal" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('pages.billing.currency', { defaultValue: 'Billing currency' })}</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="sub-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!isNew && (
              <div>
                <Label>{t('pages.billing.status', { defaultValue: 'Status' })}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="sub-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['active', 'suspended', 'expired'].map((s) => (
                      <SelectItem key={s} value={s}>{t(`pages.billing.statusValue.${s}`, { defaultValue: s })}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>{t('common.cancel')}</Button>
          <Button onClick={submit} disabled={saving} data-testid="sub-submit">
            {saving
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t('common.saving')}</>
              : t('common.save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
